import React from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Images } from "../../../utils/Images";

const MONTHLY_RED = "#FF4A60"; // Figma primary CTA (node 105:2332)
const BRAND_BLUE = "#006FDE";
const DARK_CHIP = "#3B3A3A";

const BTN_BASE = {
  width: "100%",
  height: "50px",
  border: "none",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  appearance: "none",
  WebkitAppearance: "none",
  transition: "opacity 0.15s",
  "&:hover": { opacity: 0.88 },
};

// Figma: buttons share Bold 18px / 0.36px on desktop, 14px / 0.28px on mobile.
const BTN_TEXT = {
  fontFamily: "Red Hat Display",
  fontWeight: 700,
  fontSize: { xs: "14px", sm: "18px" },
  letterSpacing: { xs: "0.28px", sm: "0.36px" },
  whiteSpace: "nowrap",
};

const paymentOptions = [
  { name: "PhonePe", src: Images.phonepayIcon },
  { name: "Gpay", src: Images.gpayIcon },
  { name: "Bhim UPI", src: Images.upiIcon },
  { name: "More", src: Images.moreIcon },
];

const MonthlyUpsellStep = ({
  totalAmount,
  currency,
  onBack,
  onMonthly,
  onKeepOnce,
}) => {
  const monthlyAmount = Math.round(totalAmount / 1.2);
  // Second monthly option — half of the calculated monthly amount.
  const lowerMonthlyAmount = Math.round(monthlyAmount / 2);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* ── Back ── */}
      <Box
        component="button"
        onClick={onBack}
        sx={{
          ...BTN_BASE,
          width: "auto",
          height: "auto",
          justifyContent: "flex-start",
          gap: "8px",
          backgroundColor: "transparent",
          p: 0,
          mb: "26px",
          "&:hover": { opacity: 0.7 },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: "16px", color: "#B9B9B8" }} />
        <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: "14px", color: "#B9B9B8" }}>
          Back
        </Typography>
      </Box>

      {/* ── Title ── */}
      <Typography
        sx={{
          fontFamily: "Red Hat Display",
          fontWeight: 700,
          fontSize: { xs: "18px", sm: "24px" },
          color: "#000000",
          lineHeight: 1.25,
          mb: { xs: "16px", sm: "18px" },
        }}
      >
        Become Our Monthly Donor And Bring Hope?
      </Typography>

      {/* ── Description (two paragraphs, matching Figma line breaks) ── */}
      <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 400, fontSize: { xs: "14px", sm: "16px" }, color: "#1D1D1B", lineHeight: "24px", mb: { xs: "16px", sm: "20px" } }}>
        Become our monthly donor and bring hope to children fighting critical illnesses, abandoned elders, and families in distress.
      </Typography>
      <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 400, fontSize: { xs: "14px", sm: "16px" }, color: "#1D1D1B", lineHeight: "24px", mb: { xs: "28px", sm: "34px" } }}>
        Your recurring support ensures that help reaches those who need it most, every single month.
      </Typography>

      {/* ── Buttons (Figma gap: 12px mobile / 25px desktop) ── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "12px", sm: "25px" }, mb: { xs: "28px", sm: "40px" } }}>
        {/* Monthly — red */}
        <Box
          component="button"
          onClick={() => onMonthly(monthlyAmount)}
          sx={{
            ...BTN_BASE,
            backgroundColor: MONTHLY_RED,
            gap: { xs: "14px", sm: "20px" },
          }}
        >
          <Box
            component="img"
            src={Images.donateMonthlySmile}
            alt=""
            sx={{ width: "18px", height: "18px", filter: "brightness(0) invert(1)" }}
          />
          <Typography sx={{ ...BTN_TEXT, color: "#FFFFFF" }}>
            Donate {currency.symbol}{monthlyAmount.toLocaleString()}/Monthly
          </Typography>
        </Box>

        {/* Lower monthly — blue (50% of the calculated monthly) */}
        <Box
          component="button"
          onClick={() => onMonthly(lowerMonthlyAmount)}
          sx={{ ...BTN_BASE, backgroundColor: BRAND_BLUE }}
        >
          <Typography sx={{ ...BTN_TEXT, color: "#FFFFFF" }}>
            Donate {currency.symbol}{lowerMonthlyAmount.toLocaleString()}/Monthly
          </Typography>
        </Box>

        {/* Keep one-time — black outline */}
        <Box
          component="button"
          onClick={onKeepOnce}
          sx={{
            ...BTN_BASE,
            backgroundColor: "#FFFFFF",
            border: "1px solid #000000",
            "&:hover": { opacity: 1, backgroundColor: "#FAFAFA" },
          }}
        >
          <Typography sx={{ ...BTN_TEXT, color: "#000000" }}>
            Keep my one-time {currency.symbol}{totalAmount.toLocaleString()} gift
          </Typography>
        </Box>
      </Box>

      {/* ── Donate Via ── */}
      <Box>
        <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: { xs: "14px", sm: "16px" }, color: "#000000", mb: "7px" }}>
          Donate Via
        </Typography>
        <Box
          sx={{
            backgroundColor: DARK_CHIP,
            borderRadius: "15px",
            p: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {paymentOptions.map((option) => (
            <Box
              key={option.name}
              onClick={onKeepOnce}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                width: "57px",
                "&:hover": { opacity: 0.8 },
              }}
            >
              <Box component="img" src={option.src} alt={option.name} sx={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }} />
              <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 400, fontSize: { xs: "12px", sm: "14px" }, color: "#FFFFFF", textAlign: "center", lineHeight: "16.8px" }}>
                {option.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default MonthlyUpsellStep;
